import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/AuthStore'
import { useEmployerStore } from '../stores/EmployerStore'
import { useQuestionsStore } from '../stores/QuestionsStore'
import { useRenewalEmployerStore } from '../stores/RenewalEmployerStore'
import { useActionCenterStore } from '../stores/ActionCenterStore'
import { useAdminCenterStore } from '../stores/AdminCenterStore'
import { useTransformLearningStore } from '../stores/TransformLearningStore'
import { useNavigationStore } from '../stores/NavigationStore'
import { useSessionStorageStore } from '../stores/SessionStorage'
import { handleErrors } from '../utils/errorHandler'
import { resetAgencyBranding } from './useAgencyBranding'
import { useLessonThumbnails } from './useLessonThumbnails'
import { useTransformOwnerContext } from './useTransformOwnerContext'

/**
 * Centralised logout composable.
 * Resets every domain store, signs out from Firebase Auth,
 * cleans browser / session state and redirects to the login page.
 *
 * Must be called inside a component's setup() so that useRouter() resolves.
 */
export function useLogout() {
  const router = useRouter()

  async function logout() {
    try {
      // 1. Reset all domain stores
      const employerStore = useEmployerStore()
      employerStore.userData = {}
      employerStore.resetAll()

      useQuestionsStore().resetQuestionStore()
      useRenewalEmployerStore().resetRenewalStore()
      useActionCenterStore().resetStore()
      useAdminCenterStore().resetAll()
      useTransformLearningStore().reset()
      // Module-level refs survive an SPA logout/login cycle — clear the admin
      // view-as context so the next user never inherits it.
      useTransformOwnerContext().exit()
      useLessonThumbnails().resetThumbnails()
      resetAgencyBranding()

      // 2. Reset navigation and session storage
      const navigationStore = useNavigationStore()
      navigationStore.customNavigation.currentPage = 'home'
      useSessionStorageStore().cleanAllEvents()

      // 3. Sign out from Firebase
      await useAuthStore().logout()

      // 4. Clean browser history and redirect. Preserve window.history.state:
      // this entry is the history bridge's 'current' sentinel; wiping it with {}
      // corrupts the sentinel and re-login (SPA, same page load) would start with
      // a broken Back/Forward bridge.
      window.history.replaceState(window.history.state, document.title, window.location.pathname)
      await router.replace({ name: 'login' })
    } catch (error) {
      handleErrors('logout', error)
      // Best-effort redirect — ignore navigation failures since the
      // original error was already reported above.
      if (router.currentRoute.value.name !== 'login') {
        await router.replace({ name: 'login' }).catch(() => {})
      }
    }
  }

  return { logout }
}
